import { Bar } from 'react-chartjs-2';
import DashboardBlock from '../DashboardBlock/DashboardBlock';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { Spinner } from '~/ui';
import i18n from '~/i18n';
import { colorsArray } from '../colorsArray/colorsArray';
import styles from './FeedbackBarChart.module.scss';
import { feedbackChartOptions } from './feedbackChartOptions';
import { FeedbackCategoryName } from '~/utils/types/dashboard';

export interface IFeedbackProps {
  categories: {
    category_id: number;
    category_name: string;
    estimation_count: number;
    estimation_less_three: number;
    percent: number;
    average: number;
    ratings: {
      bad: number;
      excellent: number;
      good: number;
      normal: number;
      terrible: number;
    };
  }[];
  total: {
    total_count: number;
    average: number;
  };
}

interface IDashboardProps {
  startDate: string;
  endDate: string;
  assetsIds: [] | number[];
  accommodation_ids: number[];
  organization_ids: number[];
}

const categoryNameTransform = (name: string) => {
  let nameTransform;

  switch (name) {
    case FeedbackCategoryName.FOOD:
      nameTransform = i18n.t('nutrition');
      break;
    case FeedbackCategoryName.ACCOMMODATION:
      nameTransform = i18n.t('accommodations');
      break;
    case FeedbackCategoryName.THICKET:
      nameTransform = i18n.t('applications');
      break;
    case FeedbackCategoryName.FREE_FORM:
      nameTransform = i18n.t('free_form');
      break;

    default:
      nameTransform = name;
      break;
  }
  return nameTransform;
};

const FeedbackBarChart = ({
  startDate,
  endDate,
  assetsIds,
  accommodation_ids,
  organization_ids,
}: IDashboardProps) => {
  const { t } = useTranslation();
  const toastError = useToastError();

  const params = {
    time: [startDate, endDate],
    asset_ids: assetsIds,
    accommodation_ids,
    organization_ids,
  };

  const { data: feedbackData, isLoading } = useQuery<IFeedbackProps>({
    queryFn: async () => {
      const { data } = await instance.get('admin/dashboard/feedbacks', {
        params,
      });

      return data;
    },
    queryKey: ['dashboard-feedbacks', params],
    onError: error => {
      toastError(error);
    },
    enabled: !!startDate && !!endDate,
  });

  const feedbackDatasets = feedbackData?.categories?.map((item, idx) => {
    const data = Array(feedbackData?.categories.length).fill(0);
    data[idx] = item.estimation_count;

    return {
      label: categoryNameTransform(item.category_name),
      data,
      backgroundColor: colorsArray[idx].backgroundColor,
      hoverBackgroundColor: colorsArray[idx].hoverBackgroundColor,
      borderRadius: 8,
      barPercentage: 0.7,
    };
  });

  const data = {
    labels: feedbackData?.categories?.map(item => item?.category_id) || [],
    datasets: feedbackDatasets || [],
  };

  const options = feedbackData ? feedbackChartOptions(feedbackData) : undefined;

  return (
    <DashboardBlock
      title={t('feedback')}
      actions={
        <div className={styles.rating}>
          <p className={styles.ratingTitle}>
            {t('total_ratings')}:{' '}
            <span className={styles.ratingValue}>
              {feedbackData?.total?.total_count || null}
            </span>
          </p>
          <p className={styles.ratingTitle}>
            {t('average_rating_full')}:{' '}
            <span className={styles.ratingValue}>
              {feedbackData?.total?.average.toFixed(2) || null}
            </span>
          </p>
        </div>
      }
    >
      {isLoading && !feedbackData ? (
        <div className={styles.spinnerWrapper}>
          <Spinner />
        </div>
      ) : (
        <Bar data={data} options={options} />
      )}
    </DashboardBlock>
  );
};

export default FeedbackBarChart;
